import React, { useState, useCallback, useEffect } from 'react'; // Added useEffect
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';
// Import the CSS directly as this component is defined here
import '../components/common/Popup.css';

// --- Hook Definition ---
export function useOrderDetailsUIState() {
  // --- Popup State ---
  const [popupConfig, setPopupConfig] = useState(null); 
  const showPopup = useCallback((config) => {
      setPopupConfig(config);
  }, []);
  const hidePopup = useCallback(() => {
      setPopupConfig(null);
  }, []);

  // --- Guest List Editor State ---
  const [guestsOpenMap, setGuestsOpenMap] = useState({});
  const [guestsTextMap, setGuestsTextMap] = useState({});
  const [guestsLoadingMap, setGuestsLoadingMap] = useState({});
  const handleToggleGuests = useCallback((fileId) => {
    setGuestsOpenMap((prev) => ({
      ...prev,
      [fileId]: !prev[fileId]
    }));
  }, []);

  // --- AI Interaction State ---
  const [aiStateMap, setAiStateMap] = useState({});
  const [showAiModalForProjectId, setShowAiModalForProjectId] = useState(null);
  const [aiRequestText, setAiRequestText] = useState("");
  const [showLogsModalConfig, setShowLogsModalConfig] = useState(null);
  const [currentLogs, setCurrentLogs] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);

  const handleOpenAiModal = useCallback((projectId) => {
    setAiRequestText("");
    setShowAiModalForProjectId(projectId);
  }, []);

  const handleCloseAiModal = useCallback(() => {
    setShowAiModalForProjectId(null);
    setAiRequestText("");
  }, []);

  const handleShowLogs = useCallback((projectId, interactionId, logType = 'standard') => {
    setShowLogsModalConfig({ projectId, interactionId, logType });
    setLogsLoading(true);
    setCurrentLogs("");
  }, []);

  const handleCloseLogsModal = useCallback(() => {
    setShowLogsModalConfig(null);
    setCurrentLogs("");
    setLogsLoading(false);
  }, []);

  // --- Email Editing State ---
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState("");

  const handleEditEmailClick = useCallback((currentEmail) => {
    setEditedEmail(currentEmail || "");
    setIsEditingEmail(true);
  }, []);

  const handleCancelEditEmail = useCallback((originalEmail) => {
    setIsEditingEmail(false);
    setEditedEmail(originalEmail || "");
  }, []);

  // --- Template Search State ---
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  // --- File Action States ---
  const [duplicatingFileId, setDuplicatingFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [addingProjectFromTemplateId, setAddingProjectFromTemplateId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // --- Metadata Modal State ---
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  const handleShowMetadata = useCallback((metadata) => {
      setSelectedMetadata(metadata);
      setShowMetadataModal(true);
  }, []);

  const handleCloseMetadata = useCallback(() => {
      setShowMetadataModal(false);
      setSelectedMetadata(null);
  }, []);

  // --- Missing Font State ---
  const [missingFontToAdd, setMissingFontToAdd] = useState(null);
  const [missingFontFile, setMissingFontFile] = useState(null);


  return {
    // Popup
    popupConfig,
    showPopup,
    hidePopup,

    // Guest List
    guestsOpenMap,
    setGuestsOpenMap,
    guestsTextMap,
    setGuestsTextMap,
    guestsLoadingMap,
    setGuestsLoadingMap,
    handleToggleGuests,

    // AI Interactions
    aiStateMap,
    setAiStateMap,
    showAiModalForProjectId,
    aiRequestText,
    setAiRequestText,
    showLogsModalConfig,
    currentLogs,
    setCurrentLogs,
    logsLoading,
    setLogsLoading,
    handleOpenAiModal,
    handleCloseAiModal,
    handleShowLogs,
    handleCloseLogsModal,

    // Email Editing
    isEditingEmail,
    setIsEditingEmail,
    editedEmail,
    setEditedEmail,
    handleEditEmailClick,
    handleCancelEditEmail,

    // Template Search
    templateSearchQuery,
    setTemplateSearchQuery,

    // File Action States
    duplicatingFileId,
    setDuplicatingFileId,
    deletingFileId,
    setDeletingFileId,
    addingProjectFromTemplateId,
    setAddingProjectFromTemplateId,
    isUploading,
    setIsUploading,

    // Metadata Modal
    selectedMetadata,
    showMetadataModal,
    handleShowMetadata,
    handleCloseMetadata,

    // Missing Font
    missingFontToAdd,
    setMissingFontToAdd,
    missingFontFile,
    setMissingFontFile,
  };
}
